if (!customElements.get('product-review-summary')) {
  class ProductReviewSummary extends HTMLElement {
    connectedCallback() {
      if (!this.handleReviewsUpdated) {
        this.handleReviewsUpdated = (event) => {
          if (event.detail?.productId && event.detail.productId !== String(this.dataset.productId || '0')) return;
          Promise.resolve().then(() => this.refresh());
        };
      }

      if (!this.isListeningForReviews) {
        document.addEventListener('theme:product-reviews-updated', this.handleReviewsUpdated);
        document.addEventListener('shopify:section:load', this.handleReviewsUpdated);
        document.addEventListener('shopify:section:unload', this.handleReviewsUpdated);
        this.isListeningForReviews = true;
      }

      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:product-reviews-updated', this.handleReviewsUpdated);
      document.removeEventListener('shopify:section:load', this.handleReviewsUpdated);
      document.removeEventListener('shopify:section:unload', this.handleReviewsUpdated);
      this.isListeningForReviews = false;
    }

    refresh() {
      const realReviews = this.getRealReviewData();
      const rating = this.dataset.ratingMode === 'real'
        ? realReviews.rating
        : this.dataset.ratingMode === 'random'
        ? this.getPersistentRandomRating()
        : this.normalizeRating(this.dataset.fixedRating);
      const ratingText = rating.toFixed(1);
      const visualRating = Math.floor(rating * 2) / 2;

      this.dataset.rating = ratingText;
      this.style.setProperty('--review-rating-width', `${this.getFillPercentage(visualRating)}%`);

      const ratingElement = this.querySelector('[data-review-rating]');
      if (ratingElement) ratingElement.textContent = ratingText;

      const stars = this.querySelector('[data-review-stars]');
      if (stars?.getAttribute('aria-hidden') !== 'true') {
        stars?.setAttribute('aria-label', this.getRatingLabel(ratingText));
      }

      const count = this.dataset.countMode === 'real'
        ? realReviews.count
        : this.dataset.countMode === 'fixed'
        ? Math.max(0, Math.trunc(Number(this.dataset.fixedCount) || 0))
        : this.getPersistentRandomCount();

      this.hidden = this.dataset.hideWithoutReviews === 'true' && realReviews.count === 0;

      const countElement = this.querySelector('[data-review-count]');
      if (countElement) {
        countElement.textContent = new Intl.NumberFormat(this.dataset.languageLocale || undefined).format(count);
      }

      const target = this.querySelector('[data-review-target]');
      const text = this.querySelector('[data-review-text]');
      if (target) {
        const accessibleText = text?.textContent?.trim();
        target.setAttribute(
          'aria-label',
          `${this.getRatingLabel(ratingText)}${accessibleText ? ` — ${accessibleText}` : ''}`
        );
      }
    }

    getRatingLabel(ratingText) {
      return (this.dataset.ratingLabel || '[rating] / 5').replace('[rating]', ratingText);
    }

    getRealReviewData() {
      const productId = String(this.dataset.productId || '0');
      const reviewSections = Array.from(document.querySelectorAll('theme-product-reviews'))
        .filter((section) => String(section.dataset.productId || '0') === productId);
      const count = reviewSections.reduce(
        (total, section) => total + Math.max(0, Math.trunc(Number(section.dataset.blockCount) || 0)),
        0
      );
      const ratingTotal = reviewSections.reduce(
        (total, section) => total + Math.max(0, Number(section.dataset.realRatingTotal) || 0),
        0
      );

      return {
        count,
        rating: count > 0 ? this.normalizeRating(ratingTotal / count) : 0
      };
    }

    normalizeRating(value) {
      const rating = Number(value);
      return Math.min(5, Math.max(0, Number.isFinite(rating) ? rating : 0));
    }

    getPersistentRandomRating() {
      const configuredMin = Math.round(this.normalizeRating(this.dataset.ratingRandomMin) * 10);
      const configuredMax = Math.round(this.normalizeRating(this.dataset.ratingRandomMax) * 10);
      const min = Math.min(configuredMin, configuredMax);
      const max = Math.max(configuredMin, configuredMax);
      const rangeSize = max - min + 1;
      const productId = String(this.dataset.productId || '0');
      const storageKey = `mavella:review-ratings:v1:${min}:${max}`;

      try {
        const assignments = this.readAssignments(storageKey);
        const storedRating = Number(assignments[productId]);

        if (Number.isInteger(storedRating) && storedRating >= min && storedRating <= max) {
          return storedRating / 10;
        }

        const initialRating = Math.round(this.normalizeRating(this.dataset.rating) * 10);
        const rating = initialRating >= min && initialRating <= max
          ? initialRating
          : min + (this.hashProductId(productId) % rangeSize);

        assignments[productId] = rating;
        localStorage.setItem(storageKey, JSON.stringify(assignments));
        return rating / 10;
      }
      catch (error) {
        return (min + (this.hashProductId(productId) % rangeSize)) / 10;
      }
    }

    getFillPercentage(rating) {
      const fullStars = Math.floor(rating);
      const hasHalfStar = rating - fullStars >= 0.5;
      const isCommon = this.querySelector('[data-review-stars]')?.classList.contains('product-review-summary__stars--common');
      const starWidth = isCommon ? 18 : 96;
      const gap = isCommon ? 4 : 8;
      const totalWidth = isCommon ? 106 : 512;
      let activeExtent = fullStars > 0
        ? (fullStars * starWidth) + ((fullStars - 1) * gap)
        : 0;

      if (hasHalfStar && fullStars < 5) {
        activeExtent += (fullStars > 0 ? gap : 0) + (starWidth / 2);
      }

      return (activeExtent / totalWidth) * 100;
    }

    getPersistentRandomCount() {
      const configuredMin = Math.trunc(Number(this.dataset.randomMin) || 0);
      const configuredMax = Math.trunc(Number(this.dataset.randomMax) || configuredMin);
      const min = Math.max(0, Math.min(configuredMin, configuredMax));
      const max = Math.max(min, configuredMin, configuredMax);
      const rangeSize = max - min + 1;
      const productId = String(this.dataset.productId || '0');
      const storageKey = `mavella:review-counts:v1:${min}:${max}`;

      try {
        const assignments = this.readAssignments(storageKey);
        const usedByOtherProducts = new Set(
          Object.entries(assignments)
            .filter(([assignedProductId]) => assignedProductId !== productId)
            .map(([, value]) => Number(value))
            .filter(Number.isInteger)
        );
        const storedCount = Number(assignments[productId]);

        if (Number.isInteger(storedCount) && storedCount >= min && !usedByOtherProducts.has(storedCount)) {
          return storedCount;
        }

        let count = min + (this.hashProductId(productId) % rangeSize);
        let attempts = 0;

        while (usedByOtherProducts.has(count) && attempts < rangeSize) {
          count = min + ((count - min + 1) % rangeSize);
          attempts += 1;
        }

        if (usedByOtherProducts.has(count)) {
          count = max + 1;
          while (usedByOtherProducts.has(count)) count += 1;
        }

        assignments[productId] = count;
        localStorage.setItem(storageKey, JSON.stringify(assignments));
        return count;
      }
      catch (error) {
        return min + (this.hashProductId(productId) % rangeSize);
      }
    }

    readAssignments(storageKey) {
      const storedValue = localStorage.getItem(storageKey);
      if (!storedValue) return {};

      const parsedValue = JSON.parse(storedValue);
      return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
        ? parsedValue
        : {};
    }

    hashProductId(productId) {
      let hash = 2166136261;

      for (let index = 0; index < productId.length; index += 1) {
        hash ^= productId.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }

      return hash >>> 0;
    }
  }

  customElements.define('product-review-summary', ProductReviewSummary);
}
